import { useCallback, useEffect, useMemo, useState } from 'react';
import useGlobals from './useGlobals';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CodeFunction, PromptFunction } from '../Types';
import { connectorService } from '../Services/connectorService';
import { mcpService } from '../Services/mcpService';

const usePromptFunction = () => {
    const { handleNavigate, userSettings, i18n } = useGlobals();

    let { promptFunctionIdentifier } = useParams();
    const [promptFunction, setPromptFunction] = useState<Partial<PromptFunction>>({
        type: 'code',
        openApiEndpoints: [],
        mcpTools: [],
    });

    const [codeFunctions, setCodeFunctions] = useState<CodeFunction[]>([]);
    const [imagePreview, setImagePreview] = useState<any>(null);
    const [selectedOptions, setSelectedOptions] = useState<any>({});
    const [selectedArguments, setSelectedArguments] = useState<any>({});
    const [possibleOptions, setPossibleOptions] = useState<[string, any][]>([]);
    const [possibleArguments, setPossibleArguments] = useState<[string, any][]>([]);
    const [openApiConfigs, setOpenApiConfigs] = useState<any[]>([]);
    const [mcpConfigs, setMcpConfigs] = useState<any[]>([]);
    const [promptFunctionLoaded, setPromptFunctionLoaded] = useState({
        promptFunctionLoaded: false,
        codeFunctionsLoaded: false,
        openApiLoaded: false,
        mcpLoaded: false,
    });
    const [promptFunctionSnapshot, setPromptFunctionSnapshot] = useState<PromptFunction | null>(null);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'toast', sourceName: 'PromptFunction' }));
    }, [i18n]);

    const handleChangePromptFunction = useCallback((key: string, value: any) => {
        setPromptFunction((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const handleCodeFunctionDropdownChange = useCallback(
        (value: string) => {
            handleChangePromptFunction('name', value);

            const selectedCodeFunction = codeFunctions.find((cf) => cf.name === value);

            if (selectedCodeFunction) {
                setSelectedOptions({});
                const formattedArguments = Object.entries(selectedCodeFunction.arguments).reduce(
                    (codeFunctionArguments, [key, value]) => {
                        codeFunctionArguments[key] = {
                            ...value,
                            description: '',
                        };
                        return codeFunctionArguments;
                    },
                    {} as { [key: string]: { type: string; description: string; required: boolean } },
                );

                setSelectedArguments(formattedArguments);
            }
        },
        [codeFunctions, handleChangePromptFunction],
    );

    const handleImageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImagePreview(URL.createObjectURL(file));
                handleChangePromptFunction('image', file);
            }
        },
        [handleChangePromptFunction],
    );

    const validateRequiredFields = useCallback(() => {
        if (!promptFunction.title || promptFunction.title.trim() === '') {
            alert(i18n.translate({ id: 'alert.title', sourceName: 'PromptFunction' }));
            return false;
        }

        if (!promptFunction.description || promptFunction.description.trim() === '') {
            alert(i18n.translate({ id: 'alert.description', sourceName: 'PromptFunction' }));
            return false;
        }

        // Validate based on connector type
        if (promptFunction.type === 'code') {
            const selectedFunction = codeFunctions.find((cf) => cf.name === promptFunction.name);

            if (!selectedFunction || promptFunction.name === '---') {
                alert(i18n.translate({ id: 'alert.selectFunc', sourceName: 'PromptFunction' }));
                return false;
            }

            for (let [key, data] of Object.entries(selectedFunction.possibleOptions)) {
                if (data.required && (!selectedOptions[key] || selectedOptions[key].trim() === '')) {
                    alert(`Das Feld "${key}" ist erforderlich und darf nicht leer sein.`);
                    return false;
                }
            }

            for (let [key, data] of Object.entries(selectedFunction.arguments)) {
                if (
                    data.required &&
                    (!selectedArguments[key]?.description || selectedArguments[key]?.description.trim() === '')
                ) {
                    alert(`Das Argument "${key}" ist erforderlich und darf nicht leer sein.`);
                    return false;
                }
            }
        } else if (promptFunction.type === 'openapi') {
            if (!promptFunction.openApiEndpoints || promptFunction.openApiEndpoints.length === 0) {
                alert(i18n.translate({ id: 'alert.openApiEndpoints', sourceName: 'PromptFunction' }));
                return false;
            }
        } else if (promptFunction.type === 'mcp') {
            if (!promptFunction.mcpTools || promptFunction.mcpTools.length === 0) {
                alert(i18n.translate({ id: 'alert.mcpTools', sourceName: 'PromptFunction' }));
                return false;
            }
        }

        return true;
    }, [selectedOptions, selectedArguments, codeFunctions, i18n, promptFunction]);

    const handleSave = useCallback(() => {
        if (!validateRequiredFields()) {
            return;
        }

        if (!userSettings.accessToken) return;
        const data = new FormData();
        data.append('title', promptFunction.title ?? '');
        data.append('name', promptFunction.name ?? '');
        data.append('description', promptFunction.description ?? '');
        data.append('type', promptFunction.type ?? 'code');
        data.append('options', JSON.stringify(selectedOptions));
        data.append('arguments', JSON.stringify(selectedArguments));

        // Add OpenAPI endpoints for openapi type connectors
        if (promptFunction.type === 'openapi' && promptFunction.openApiEndpoints) {
            promptFunction.openApiEndpoints.forEach((endpoint) => {
                data.append('openApiEndpoints[]', endpoint);
            });
        }

        // Add MCP tools for mcp type connectors
        if (promptFunction.type === 'mcp' && promptFunction.mcpTools) {
            promptFunction.mcpTools.forEach((tool) => {
                data.append('mcpTools[]', tool);
            });
        }

        if (promptFunction.image instanceof File || promptFunction.image instanceof Blob) {
            const fileType = promptFunction.image.type.split('/')[1];
            const fileName = `upload.${fileType}`;
            data.append('image', new File([promptFunction.image], fileName, { type: promptFunction.image.type }));
        } else {
            // TODO: Micha fragen ob es gewollt ist, dass ein Bild nicht entfernt werden kann
            // data.append('image', '');
        }

        let urlPart = promptFunctionIdentifier === 'new' ? 'create' : 'update/' + promptFunctionIdentifier;

        connectorService.saveConnector(urlPart, data).then((response: { data: PromptFunction }) => {
            promptFunctionIdentifier === 'new' &&
                handleNavigate('/admin/assistantfunctions/' + response.data.identifier);
            setPromptFunctionSnapshot(response.data);
            notifySuccess();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [
        promptFunction,
        selectedOptions,
        selectedArguments,
        userSettings.accessToken,
        validateRequiredFields,
        promptFunctionIdentifier,
        handleNavigate,
        notifySuccess,
    ]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        if (promptFunctionIdentifier !== 'new' && promptFunctionIdentifier !== undefined) {
            connectorService.getConnector(promptFunctionIdentifier).then((response: { data: PromptFunction }) => {
                setPromptFunction(response.data);
                if (response.data.image) {
                    setImagePreview(response.data.image);
                }

                if (response.data.options) {
                    setSelectedOptions(response.data.options);
                }
                if (response.data.arguments) {
                    setSelectedArguments(response.data.arguments);
                }
                setPromptFunctionLoaded((prevState) => ({ ...prevState, promptFunctionLoaded: true }));
                setPromptFunctionSnapshot(response.data);
            });
        } else {
            setPromptFunctionLoaded((prevState) => ({ ...prevState, promptFunctionLoaded: true }));
        }

        connectorService.getCodeFunctions().then((response) => {
            setCodeFunctions(response.data);
            setPromptFunctionLoaded((prevState) => ({ ...prevState, codeFunctionsLoaded: true }));
        });

        // Load OpenAPI configs
        import('../Services/openApiService')
            .then(({ openApiService }) => {
                openApiService
                    .getConfigs(false, 1, 100)
                    .then((response: any) => {
                        const configs = response.data.data || [];
                        setOpenApiConfigs(configs);
                        setPromptFunctionLoaded((prevState) => ({ ...prevState, openApiLoaded: true }));
                    })
                    .catch((error: any) => {
                        console.error('Failed to load OpenAPI configs:', error);
                        setPromptFunctionLoaded((prevState) => ({ ...prevState, openApiLoaded: true }));
                    });
            })
            .catch((error: any) => {
                console.error('Failed to import openApiService:', error);
                setPromptFunctionLoaded((prevState) => ({ ...prevState, openApiLoaded: true }));
            });

        // Load MCP configs
        mcpService
            .getConfigs(false, 1, 100)
            .then((response) => {
                const configs = response.data.data || [];
                setMcpConfigs(configs);
                setPromptFunctionLoaded((prevState) => ({ ...prevState, mcpLoaded: true }));
            })
            .catch((error) => {
                setPromptFunctionLoaded((prevState) => ({ ...prevState, mcpLoaded: true }));
            });
    }, [userSettings.isLoggedIn, promptFunctionIdentifier]);

    useEffect(() => {
        const selectedFunction = codeFunctions.find((cf: CodeFunction) => cf.name === promptFunction.name);
        setPossibleOptions(selectedFunction?.possibleOptions ? Object.entries(selectedFunction.possibleOptions) : []);
        setPossibleArguments(selectedFunction?.arguments ? Object.entries(selectedFunction.arguments) : []);
    }, [promptFunctionIdentifier, promptFunction, codeFunctions]);

    const PromptFunctionActions = useMemo(
        () => ({
            handleCodeFunctionDropdownChange,
            handleImageChange,
            handleSave,
        }),
        [handleCodeFunctionDropdownChange, handleImageChange, handleSave],
    );

    return {
        PromptFunctionActions,
        handleNavigate,
        imagePreview,
        possibleArguments,
        possibleOptions,
        promptFunction,
        i18n,
        codeFunctions,
        selectedOptions,
        selectedArguments,
        promptFunctionLoaded,
        promptFunctionSnapshot,
        handleChangePromptFunction,
        setSelectedOptions,
        setSelectedArguments,
        promptFunctionIdentifier,
        openApiConfigs,
        mcpConfigs,
    };
};

export default usePromptFunction;
